import { PDFOptions } from 'puppeteer-core';
import BwipJs from 'bwip-js';
import { IGerarPDFItau } from './types';
import maskFormat from '@/utils/maskFormat';
import { compileHbs } from '@/utils/compileHbs';
import { getPathTemplates } from '@/utils/getPathTemplates';
import { api } from '@/services/api';
import { decompress } from '@/utils/zip';
import { copy } from '@/utils/copy';

export async function gerarPDF({ dados }: IGerarPDFItau) {
  try {
    const { dadosPDF } = dados;

    if (!dadosPDF.pagador.cnpj) {
      throw new Error('CPF/CNPJ do pagador não informado!');
    }

    const codigoBarra = await BwipJs.toBuffer({
      bcid: 'interleaved2of5',
      text: dadosPDF.boleto.codigoBarra,
      height: 20,
    });

    dadosPDF.beneficiario.dadosBancario.agencia =
      dadosPDF.beneficiario.dadosBancario.agencia.padStart(4, '0');

    dadosPDF.boleto.linhaDigitavel = maskFormat.codigoBarra(dadosPDF.boleto.linhaDigitavel);
    dadosPDF.beneficiario.cnpj = maskFormat.retornaCpfCnpjFormatado(
      dadosPDF.beneficiario.cnpj,
    );
    dadosPDF.beneficiario.endereco.cep = maskFormat.retornaCEPFormatado(
      dadosPDF.beneficiario.endereco.cep,
    );
    dadosPDF.pagador.endereco.cep = maskFormat.retornaCEPFormatado(
      dadosPDF.pagador.endereco.cep,
    );
    dadosPDF.pagador.cnpj = maskFormat.retornaCpfCnpjFormatado(dadosPDF.pagador.cnpj);

    const valorDocumento = Number(dadosPDF.boleto.valorDocumento);
    dadosPDF.boleto.valorDocumento = maskFormat.numberToReal(
      dadosPDF.boleto.valorDocumento,
    );

    const jurosMes = dadosPDF.beneficiario.dadosBancario.juros;
    const jurosDia = (jurosMes / 100 / 30) * valorDocumento;

    if (jurosDia > 0) {
      dadosPDF.boleto.instrucoes = [`APÓS O VENCIMENTO, COBRAR JUROS DE R$${maskFormat.numberToReal(jurosDia)} AO DIA`, ...dadosPDF.boleto.instrucoes];
    }

    const descontoVencimento = dadosPDF.beneficiario.dadosBancario.desconto || 0;
    if (descontoVencimento > 0) {
      dadosPDF.boleto.instrucoes = [`ATÉ O VENCIMENTO, CONCEDER DESCONTO DE ${descontoVencimento}%`, ...dadosPDF.boleto.instrucoes];
    }

    const multaAtraso = dadosPDF.beneficiario.dadosBancario.multa || 0;
    if (multaAtraso > 0) {
      dadosPDF.boleto.instrucoes = [`MULTA DE ${multaAtraso}%`, ...dadosPDF.boleto.instrucoes];
    }

    const protestoDias = dadosPDF.beneficiario.dadosBancario.protesto || 0;
    if (protestoDias > 0) {
      dadosPDF.boleto.instrucoes = [`PROTESTAR APÓS ${protestoDias} DIAS CORRIDOS DO VENCIMENTO`, ...dadosPDF.boleto.instrucoes];
    }

    const content = compileHbs(getPathTemplates('itau-boleto.hbs'), {
      ...dadosPDF,
      boleto: {
        ...dadosPDF.boleto,
        codigoBarraImage: codigoBarra.toString('base64'),
        nossoNumeroDigito: getDigito(`${dadosPDF.beneficiario.dadosBancario.agencia}${dadosPDF.beneficiario.dadosBancario.conta}${dadosPDF.beneficiario.dadosBancario.codigoCarteira}${dadosPDF.boleto.nossoNumero}`),
      }
    });

    const config: PDFOptions = {
      displayHeaderFooter: false,
      path: './boleto.pdf',
      format: 'a4',
      printBackground: true,
    };

    const response = await api.post('/', { content, config });
    const buffer = Buffer.from(response.data.pdf);
    const pdfBase64 = await decompress(buffer);

    return {
      erro: false,
      dados: pdfBase64,
    };
  } catch (error) {
    console.log(error);
    return {
      erro: true,
      dados: 'Falha ao gerar impressão do boleto',
    };
  }
}

function getDigito(texto: string) {
  let i = texto.length;
  let j = 1;
  let total = 0;
  let soma = 0;
  let digito = 0;

  while (i >= 1) {
    j = j === 1 ? 2 : 1;

    let valor = Number(copy(texto, i, i));
    total = valor * j;

    if (total >= 10) {
      valor = Number(copy(total, 1, 1)) + Number(copy(total, 2, 2));
    } else {
      valor = total;
    }

    soma += valor;
    i--;
  }

  const uni = Number(copy(soma, String(soma).length, String(soma).length));
  digito = 10 - uni;
  if (digito === 10) digito = 0;
  return String(digito);
}
